import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { METRO_NAMES } from "@/lib/metros";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarClock, Plus, ExternalLink, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["NOFO","RFP","LIHTC","Voucher","Contract","Grant"];
const VERTICALS = ["veteran","psh","rapid_rehousing","transitional","recovery","reentry","dv","youth","behavioral","affordable","rural","senior","disability"];

const urgencyStyle = (days) => {
  if (days <= 14) return { badge: "bg-rose-50 text-rose-700 border-rose-200", label: `${days}d left · Urgent`, pill: "bg-rose-600" };
  if (days <= 45) return { badge: "bg-amber-50 text-amber-700 border-amber-200", label: `${days}d left`, pill: "bg-amber-500" };
  return { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `${days}d left`, pill: "bg-emerald-600" };
};

export default function Funding() {
  const [items, setItems] = useState([]);
  const [metro, setMetro] = useState("All Metros");
  const [sort, setSort] = useState("soonest");
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  const [form, setForm] = useState({
    name: "", funder: "", type: "NOFO", metros: [], verticals: [],
    deadline: "", status: "Open", url: "", notes: "",
  });

  const load = async () => {
    const r = await api.get("/funding");
    const now = Date.now();
    const enriched = r.data.map(f => {
      const dl = f.deadline ? new Date(f.deadline).getTime() : null;
      return { ...f, days_until: dl ? Math.round((dl - now) / (1000 * 60 * 60 * 24)) : null };
    });
    setItems(enriched);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.deadline) return toast.error("Name and deadline required");
    await api.post("/funding", { ...form, deadline: new Date(form.deadline).toISOString() });
    toast.success("Funding opportunity added"); setOpen(false); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this funding opportunity?")) return;
    await api.delete(`/funding/${id}`); load();
  };
  const toggle = (key, v) => setForm(f => ({...f, [key]: f[key].includes(v) ? f[key].filter(x=>x!==v) : [...f[key], v]}));

  const filtered = useMemo(() => {
    let out = items.filter(i =>
      metro === "All Metros" || (i.metros || []).some(m => m === metro || (metro === "Dallas–Fort Worth" && m === "Dallas-Fort Worth"))
    );
    out = out.slice().sort((a, b) => {
      const A = a.days_until ?? 9999, B = b.days_until ?? 9999;
      if (sort === "soonest") return A - B;
      if (sort === "latest") return B - A;
      if (sort === "type") return String(a.type).localeCompare(String(b.type));
      return 0;
    });
    return out;
  }, [items, metro, sort]);

  const upcoming = filtered.filter(i => i.days_until !== null && i.days_until >= 0);
  const past = filtered.filter(i => i.days_until !== null && i.days_until < 0);

  return (
    <div className="space-y-6" data-testid="funding-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5"/>Funding Pipeline</div>
          <h1 className="text-3xl font-bold">Funding Calendar</h1>
          <p className="text-xs text-slate-500 mt-1">NOFOs, RFPs, LIHTC windows, voucher turnovers and contract deadlines across Texas metros.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-funding-btn"><Plus className="w-4 h-4 mr-1"/>Add Opportunity</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Funding Opportunity</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="fn-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
              <div><Label>Funder</Label><Input value={form.funder} onChange={e=>setForm({...form, funder:e.target.value})}/></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v=>setForm({...form, type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={e=>setForm({...form, deadline:e.target.value})} data-testid="fn-deadline"/></div>
              <div><Label>Status</Label><Input value={form.status} onChange={e=>setForm({...form, status:e.target.value})}/></div>
              <div className="col-span-2"><Label>Metros</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {METRO_NAMES.filter(m=>m!=="All Metros").map(m => (
                    <button key={m} type="button" onClick={()=>toggle("metros", m)}
                      className={`px-3 py-1 rounded-full text-xs border ${form.metros.includes(m) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>{m}</button>
                  ))}
                </div>
              </div>
              <div className="col-span-2"><Label>Verticals</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {VERTICALS.map(v => (
                    <button key={v} type="button" onClick={()=>toggle("verticals", v)}
                      className={`px-3 py-1 rounded-full text-xs border capitalize ${form.verticals.includes(v) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>{v.replace("_"," ")}</button>
                  ))}
                </div>
              </div>
              <div className="col-span-2"><Label>URL</Label><Input value={form.url} onChange={e=>setForm({...form, url:e.target.value})}/></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
            </div>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="fn-submit">Save</Button>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-slate-200 flex gap-3 items-center flex-wrap">
        <Select value={metro} onValueChange={setMetro}>
          <SelectTrigger className="w-56" data-testid="funding-metro-filter"><SelectValue/></SelectTrigger>
          <SelectContent>{METRO_NAMES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-56" data-testid="funding-sort"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="soonest">Sort: Soonest first (most urgent)</SelectItem>
            <SelectItem value="latest">Sort: Latest first (most time left)</SelectItem>
            <SelectItem value="type">Sort: Type</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 font-mono ml-auto">{upcoming.length} upcoming · {past.length} past</div>
      </Card>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="funding-view-list">List</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="funding-view-calendar">Calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="mt-4 space-y-3">
          {upcoming.map(f => {
            const u = urgencyStyle(f.days_until);
            return (
              <Card key={f.id} className="p-4 border-slate-200" data-testid={`funding-card-${f.id}`}>
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.type}</span>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-full ${u.badge}`}>{u.label}</span>
                      {(f.metros||[]).map(m => <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{m}</span>)}
                    </div>
                    <h3 className="font-semibold text-slate-900">{f.name}</h3>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.funder} · Deadline {new Date(f.deadline).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                    </div>
                    {f.notes && <p className="text-xs text-slate-600 mt-2">{f.notes}</p>}
                    <div className="flex gap-3 mt-2 text-xs">
                      {(f.verticals||[]).map(v => <span key={v} className="text-slate-500 capitalize">#{v.replace("_"," ")}</span>)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {f.url && <Button size="sm" variant="outline" asChild><a href={f.url} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1"/>Source</a></Button>}
                    <Button size="sm" variant="ghost" onClick={()=>remove(f.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              </Card>
            );
          })}
          {upcoming.length === 0 && <div className="text-sm text-slate-500">No upcoming opportunities for this metro.</div>}
          {past.length > 0 && (
            <>
              <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mt-8">Past deadlines</h2>
              <div className="space-y-2 opacity-60">
                {past.map(f => (
                  <Card key={f.id} className="p-3 border-slate-200">
                    <div className="flex justify-between items-center">
                      <div><span className="text-xs text-slate-500 mr-2">{f.type}</span><span className="font-medium">{f.name}</span></div>
                      <div className="text-xs text-slate-500">{Math.abs(f.days_until)}d ago</div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
        <TabsContent value="calendar" className="mt-4">
          <CalendarView month={month} onNav={setMonth} items={filtered} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CalendarView({ month, onNav, items }) {
  const y = month.getFullYear(), m = month.getMonth();
  const first = new Date(y, m, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const label = month.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const byDay = {};
  items.forEach(f => {
    if (!f.deadline) return;
    const d = new Date(f.deadline);
    if (d.getFullYear() === y && d.getMonth() === m) {
      const key = d.getDate();
      byDay[key] = byDay[key] || [];
      byDay[key].push(f);
    }
  });
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const today = new Date();
  const isToday = (day) => day && today.getFullYear() === y && today.getMonth() === m && today.getDate() === day;

  return (
    <Card className="p-5 border-slate-200" data-testid="funding-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => onNav(new Date(y, m - 1, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></button>
        <div className="text-lg font-semibold">{label}</div>
        <button onClick={() => onNav(new Date(y, m + 1, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-4 h-4"/></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="p-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => (
          <div key={i} className={`min-h-[92px] border rounded-md p-1.5 ${day ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100"}`}>
            {day && (
              <>
                <div className={`text-xs font-mono ${isToday(day) ? "bg-slate-900 text-white rounded-full w-6 h-6 flex items-center justify-center" : "text-slate-600"}`}>{day}</div>
                <div className="mt-1 space-y-1">
                  {(byDay[day] || []).slice(0, 3).map(f => {
                    const u = urgencyStyle(f.days_until ?? 999);
                    return (
                      <div key={f.id} className={`text-[9px] leading-tight px-1.5 py-0.5 rounded text-white truncate ${u.pill}`} title={f.name}>
                        {f.name}
                      </div>
                    );
                  })}
                  {(byDay[day] || []).length > 3 && <div className="text-[9px] text-slate-500">+{(byDay[day] || []).length - 3} more</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
